import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Mail, Plus, Search } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { SenderCard } from '../components/SenderCard';
import { SenderDeleteModal } from '../components/SenderDeleteModal';
import { SenderEmptyState } from '../components/SenderEmptyState';
import { SenderForm } from '../components/SenderForm';
import { SenderValidationModal } from '../components/SenderValidationModal';
import {
  senderKeys,
  useCreateSender,
  useDeleteSender,
  useSenders,
  useUpdateSender,
} from '../hooks/useSenders';
import { useSendersStore } from '../store/senders.store';
import {
  CreateSenderDto,
  Sender,
  SenderFilter,
  UpdateSenderDto,
} from '../types/sender.types';

// // Search component with debounce
// const SearchBar = () => {
//   const { t } = useTranslation();
//   const { searchQuery, setSearchQuery } = useSendersStore();
//   const [localSearch, setLocalSearch] = useState(searchQuery);

//   // Debounce search
//   useEffect(() => {
//     const timer = setTimeout(() => {
//       setSearchQuery(localSearch);
//     }, 500);

//     return () => clearTimeout(timer);
//   }, [localSearch, setSearchQuery]);

//   return (
//     <div className="relative min-w-0 flex-1">
//       <Search className="absolute top-1/2 left-3 h-5 w-5 -translate-y-1/2 text-neutral-400" />
//       <input
//         type="text"
//         placeholder={t('senders.search.placeholder', 'Search senders...')}
//         value={localSearch}
//         onChange={e => setLocalSearch(e.target.value)}
//         className="focus:border-primary-500 focus:ring-primary-100 w-full rounded-xl border border-neutral-300 py-3 pr-4 pl-10 transition-colors focus:ring-2"
//       />
//     </div>
//   );
// };

// // Status filter component
// const StatusFilter = () => {
//   const { t } = useTranslation();
//   const { statusFilter, setStatusFilter } = useSendersStore();

//   return (
//     <div className="relative min-w-0 flex-shrink-0">
//       <select
//         value={statusFilter}
//         onChange={e => setStatusFilter(e.target.value as SenderFilter)}
//         className="focus:border-primary-500 focus:ring-primary-100 w-full appearance-none rounded-xl border border-neutral-300 bg-white px-4 py-3 pr-8 transition-colors focus:ring-2 sm:w-auto"
//       >
//         <option value="all">{t('senders.filter.all', 'All Senders')}</option>
//         <option value="verified">
//           {t('senders.filter.verified', 'Verified')}
//         </option>
//         <option value="unverified">
//           {t('senders.filter.unverified', 'Unverified')}
//         </option>
//         <option value="active">{t('senders.filter.active', 'Active')}</option>
//         <option value="inactive">
//           {t('senders.filter.inactive', 'Inactive')}
//         </option>
//       </select>
//       <ChevronDown className="pointer-events-none absolute top-1/2 right-3 h-5 w-5 -translate-y-1/2 text-neutral-400" />
//     </div>
//   );
// };

// Filter and sort senders
const filterSenders = (
  senders: Sender[],
  searchQuery: string,
  statusFilter: SenderFilter
) => {
  return senders.filter(sender => {
    // Search filter
    const matchesSearch =
      searchQuery === '' ||
      sender.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      sender.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      sender.metadata.domain.toLowerCase().includes(searchQuery.toLowerCase());

    // Status filter
    let matchesStatus = true;
    switch (statusFilter) {
      case 'verified':
        matchesStatus = sender.isVerified;
        break;
      case 'unverified':
        matchesStatus = !sender.isVerified;
        break;
      case 'active':
        matchesStatus = sender.isActive;
        break;
      case 'inactive':
        matchesStatus = !sender.isActive;
        break;
      default:
        matchesStatus = true;
    }

    return matchesSearch && matchesStatus;
  });
};

export const SendersPage = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data: senders = [], isLoading, error } = useSenders();

  // Handle OAuth callback redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oauthResult = params.get('oauth');
    if (oauthResult === 'success') {
      toast.success(t('senders.oauth.success', 'Boîte mail connectée'));
      queryClient.invalidateQueries({ queryKey: senderKeys.all });
      window.history.replaceState({}, '', window.location.pathname);
    } else if (oauthResult === 'error') {
      const message = params.get('message') || 'Erreur de connexion OAuth';
      toast.error(message);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [queryClient, t]);

  const {
    searchQuery,
    statusFilter,
    isCreateModalOpen,
    isEditModalOpen,
    isValidationModalOpen,
    isDeleteModalOpen,
    selectedSenderUuid,
    openCreateModal,
    closeCreateModal,
    closeEditModal,
    closeValidationModal,
    closeDeleteModal,
  } = useSendersStore();

  const createSenderMutation = useCreateSender();
  const updateSenderMutation = useUpdateSender();
  const deleteSenderMutation = useDeleteSender();
  // const syncWithBrevoMutation = useSyncWithBrevo();

  // Get selected sender for editing/validation
  const selectedSender = senders?.find(
    sender => sender.uuid === selectedSenderUuid
  );

  // Filter senders
  const filteredSenders = senders
    ? filterSenders(senders, searchQuery, statusFilter)
    : [];

  const handleCreateSender = async (data: CreateSenderDto) => {
    await createSenderMutation.mutateAsync(data);
    closeCreateModal();
  };

  const handleUpdateSender = async (data: UpdateSenderDto) => {
    if (!selectedSender) return;
    await updateSenderMutation.mutateAsync({ uuid: selectedSender.uuid, data });
    closeEditModal();
  };

  const handleDeleteSender = async () => {
    if (!selectedSender) return;
    await deleteSenderMutation.mutateAsync(selectedSender.uuid);
    closeDeleteModal();
  };

  // const handleSyncWithBrevo = () => {
  //   syncWithBrevoMutation.mutate();
  // };

  if (isLoading) {
    return (
      <div className="space-y-6 sm:space-y-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <div className="bg-primary-100 flex h-12 w-12 items-center justify-center rounded-2xl">
            <Mail className="text-primary-600 h-6 w-6" />
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-neutral-900 sm:text-3xl">
              {t('senders.title', 'Senders')}
            </h1>
            <p className="text-base text-neutral-600 sm:text-lg">
              {t('senders.subtitle', 'Manage your email senders')}
            </p>
          </div>
        </div>

        <div className="animate-pulse space-y-6">
          <div className="flex flex-col gap-4 sm:flex-row">
            <div className="h-12 flex-1 rounded-xl bg-neutral-200"></div>
            <div className="h-12 w-full rounded-xl bg-neutral-200 sm:w-48"></div>
          </div>
          {[...Array(3)].map((_, i) => (
            <div
              key={i}
              className="h-32 rounded-2xl bg-neutral-200 sm:h-40"
            ></div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6 sm:space-y-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <div className="bg-primary-100 flex h-12 w-12 items-center justify-center rounded-2xl">
            <Mail className="text-primary-600 h-6 w-6" />
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-neutral-900 sm:text-3xl">
              {t('senders.title', 'Senders')}
            </h1>
            <p className="text-base text-neutral-600 sm:text-lg">
              {t('senders.subtitle', 'Manage your email senders')}
            </p>
          </div>
        </div>

        <div className="border-red-200 bg-error-50 rounded-2xl border p-8 text-center sm:p-12">
          <div className="bg-error-100 mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full sm:h-16 sm:w-16">
            <Mail className="text-error-600 h-6 w-6 sm:h-8 sm:w-8" />
          </div>
          <h3 className="text-error-900 mb-2 text-lg font-semibold">
            {t('senders.error.title', 'Failed to load senders')}
          </h3>
          <p className="text-error-700 mx-auto max-w-md text-sm sm:text-base">
            {t('senders.error.description', 'Please try again later')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <div className="bg-primary-100 flex h-12 w-12 items-center justify-center rounded-2xl">
            <Mail className="text-primary-600 h-6 w-6" />
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-neutral-900 sm:text-3xl">
              {t('senders.title', 'Senders')}
            </h1>
            <p className="text-base text-neutral-600 sm:text-lg">
              {t('senders.subtitle', 'Manage your email senders')}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* <Button
            variant="outline"
            onClick={handleSyncWithBrevo}
            disabled={syncWithBrevoMutation.isPending}
          >
            <RefreshCw
              className={`mr-2 h-4 w-4 ${syncWithBrevoMutation.isPending ? 'animate-spin' : ''}`}
            />
            {t('senders.syncBrevo', 'Sync')}
          </Button> */}

          <Button onClick={openCreateModal}>
            <Plus className="mr-2 h-4 w-4" />
            {t('senders.create', 'Create Sender')}
          </Button>
        </div>
      </div>

      {senders.length === 0 ? (
        <SenderEmptyState />
      ) : (
        <>
          {/* Search and Filter */}
          {/* <div className="flex flex-col items-stretch gap-4 sm:flex-row sm:items-center">
            <SearchBar />
            <StatusFilter />
          </div> */}

          {/* Senders List */}
          <div className="space-y-4">
            {filteredSenders.length === 0 ? (
              <div className="rounded-2xl border border-neutral-200 bg-neutral-50 p-8 text-center sm:p-16">
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-neutral-100 sm:h-16 sm:w-16">
                  <Search className="h-6 w-6 text-neutral-400 sm:h-8 sm:w-8" />
                </div>
                <h3 className="mb-2 text-lg font-semibold text-neutral-900">
                  {t('senders.noResults.title', 'No senders found')}
                </h3>
                <p className="mx-auto max-w-md text-sm text-neutral-600 sm:text-base">
                  {t(
                    'senders.noResults.description',
                    'Try adjusting your search or filter criteria'
                  )}
                </p>
              </div>
            ) : (
              filteredSenders.map((sender: Sender) => (
                <SenderCard key={sender.uuid} sender={sender} />
              ))
            )}
          </div>
        </>
      )}

      {/* Create/Edit Modal */}
      <Modal
        isOpen={isCreateModalOpen || isEditModalOpen}
        onClose={isCreateModalOpen ? closeCreateModal : closeEditModal}
        title={
          isCreateModalOpen
            ? t('senders.create', 'Create Sender')
            : t('senders.edit', 'Edit Sender')
        }
        size="lg"
      >
        <SenderForm
          sender={selectedSender}
          onSubmit={data => {
            if (isCreateModalOpen) {
              handleCreateSender(data as CreateSenderDto);
            } else {
              handleUpdateSender(data as UpdateSenderDto);
            }
          }}
          onCancel={isCreateModalOpen ? closeCreateModal : closeEditModal}
          isLoading={
            createSenderMutation.isPending || updateSenderMutation.isPending
          }
        />
      </Modal>

      {/* Validation Modal */}
      <SenderValidationModal
        isOpen={isValidationModalOpen}
        onClose={closeValidationModal}
        sender={selectedSender}
      />

      {/* Delete Modal */}
      <SenderDeleteModal
        isOpen={isDeleteModalOpen}
        onClose={closeDeleteModal}
        sender={selectedSender || null}
        onConfirm={handleDeleteSender}
        isLoading={deleteSenderMutation.isPending}
      />
    </div>
  );
};
